import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { colors } from 'src/theme'
import { CancelSmallMinor, NoteMinor } from '@shopify/polaris-icons'
import { FundingInvoice } from 'src/interfaces/payment'
import { useDispatch } from 'react-redux'
import { DownloadFundingInvoice } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { PaymentInvoiceModal } from '../PaymentInvoiceModal'
import dayjs from 'dayjs'
import { showToast } from 'src/utils/toast'
import { formatCurrencyFromCents } from 'src/utils/transformers'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  isOpen?: boolean
  onClose(): void
  currentInvoice: FundingInvoice | null
  handleClearCurrentInvoice: () => void
}

const InstantFundInProgressModalComponent = ({ isOpen, onClose, currentInvoice }: Props) => {
  const dispatch = useDispatch()

  const [invoice, setInvoice] = useState<any>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleShowInvoice = useCallback(async () => {
    if (!currentInvoice) {
      return
    }

    try {
      const buffer: any = await createAsyncAction(
        dispatch,
        DownloadFundingInvoice.request({ invoiceId: currentInvoice.id }),
      )

      const blob = new Blob([buffer], { type: 'application/pdf' })

      setInvoice(URL.createObjectURL(blob))
    } catch {
      showToast(
        'Generating your invoice—this may take up to 15 seconds. Please hold on and try again soon',
      )
    }
  }, [currentInvoice])

  const clearInvoice = useCallback(() => {
    setInvoice(null)
  }, [])

  const handleDownloadInvoice = useCallback(async () => {}, [invoice])

  const totalRepayment = useMemo(() => {
    if (!currentInvoice?.amount || !currentInvoice?.gigsafeTotalFeeAmount) {
      return null
    }

    return currentInvoice.amount + currentInvoice.gigsafeTotalFeeAmount
  }, [currentInvoice?.amount, currentInvoice?.gigsafeTotalFeeAmount])

  return (
    <Fragment>
      <Modal
        container={container}
        open={Boolean(isOpen)}
        onClose={onClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex justify-center items-center backdrop-blur p-4`}
      >
        <div
          data-testid="payments-main-fund-instant-in-progress-modal"
          css={tw`bg-white rounded-lg overflow-hidden w-[424px]`}
        >
          <div css={tw`p-4 flex flex-col`}>
            <div
              data-testid="payments-main-fund-instant-in-progress-close-btn"
              css={tw`p-1 rounded bg-[#EDEDED] h-[28px] hover:bg-[#DAD9E0] active:bg-[#B4AFC0] cursor-pointer self-end`}
              onClick={onClose}
            >
              <CancelSmallMinor width={20} height={20} />
            </div>
            <GSIcon iconName="gs_check" css={tw`w-5 h-5 self-center`} />
            <Typography variant="h4" css={tw`mt-2 text-center mb-1`}>
              Settlement in process
            </Typography>
            <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mx-16 text-center`}>
              Your contractor settlements have been funded by a third-party financing partner.
            </Typography>
            <div css={tw`grid grid-cols-2 gap-6 mx-6 my-8`}>
              <div>
                <Typography css={tw`mb-1 text-center`} variant="h2">
                  {!!totalRepayment && formatCurrencyFromCents(totalRepayment)}
                </Typography>
                <Typography css={tw`text-center`} variant="body2" color={colors.SHADES_GREY_400}>
                  Total repayment
                </Typography>
              </div>
              <div>
                <Typography css={tw`mb-1 text-center whitespace-nowrap`} variant="h2">
                  {dayjs(currentInvoice?.loanDueDate).format('MMM D, YYYY')}
                </Typography>
                <Typography css={tw`text-center`} variant="body2" color={colors.SHADES_GREY_400}>
                  Repayment due
                </Typography>
              </div>
            </div>
          </div>
          <div
            css={tw`px-4 py-3 flex flex-row justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
          >
            <Button
              size="small"
              color="white"
              leftIcon={<NoteMinor width={20} />}
              onClick={handleShowInvoice}
              data-testid="payments-main-fund-instant-in-progress-show-invoice-btn"
            >
              Show invoice
            </Button>
          </div>
        </div>
      </Modal>
      <PaymentInvoiceModal
        isOpen={Boolean(invoice)}
        invoice={invoice}
        onClose={clearInvoice}
        onDownload={handleDownloadInvoice}
      />
    </Fragment>
  )
}

export const InstantFundInProgressModal = memo(InstantFundInProgressModalComponent)
